"use client";

/**
 * useSetupFlow Hook
 *
 * Manages the complete state of the setup flow including:
 * - Current step and navigation direction
 * - Form data (username, email, password, etc.)
 * - Profile and AI analysis data
 * - Loading and error states
 *
 * The hook handles step logic based on authentication status:
 * - Logged out: Steps 0 → 1 → 2 → 3 (username → analysis → account → payment)
 * - Logged in: Steps 0 → 1 → 3 (username → analysis → payment, skip account creation)
 *
 * STATE PERSISTENCE STRATEGY:
 * Profile data is persisted to sessionStorage to survive page refreshes.
 * This prevents the "No profile data available" error on step 2+.
 *
 * Layers of persistence:
 * 1. React state (fastest, in-memory)
 * 2. sessionStorage (survives refresh, auto-cleared on tab close)
 * 3. URL params (username, platform, step - for recovery)
 * 4. localStorage (OAuth flow only, expires in 10 min)
 *
 * Recovery mechanisms:
 * - On mount: restore from sessionStorage if available
 * - If step > 0 without data: check sessionStorage, then re-fetch if username available
 * - If unrecoverable: redirect to step 0
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import supabaseClient from "@/utils/supabase-browser";
import type { Platform, SetupStep, SetupState, SetupFlowState, ProfileData, AIAnalysis } from "../types";
import { normalizeSocialHandle } from "@/utils/social-handle";
import { PLAN_RESTRICTED_COUPONS } from "@/constants/coupons";
import { isYearlyPlanId } from "@/constants/pricing";
import { getMigrationExpress } from "@/lib/ps-migration";

// localStorage key for preserving setup state across OAuth redirect
const SETUP_STATE_KEY = "outfame-setup-state";
// sessionStorage key for persisting profile data across page refreshes
const PROFILE_DATA_KEY = "outfame-setup-profile";
// Max age for cached profile data (30 minutes)
const PROFILE_DATA_MAX_AGE = 30 * 60 * 1000;
const initialState: SetupState = {
  platform: "instagram",
  username: "",
  selectedAvatarUrl: null,
  profileData: null,
  aiAnalysis: null,
  email: "",
  password: "",
  activePeriod: "Monthly",
  // owner decision 2026-08-04: yearly default fed a 4.0%-approval lane (monthly 36.7%)
  selectedPlanName: null,
  isLoading: false,
  error: null
};

/**
 * Safe hook to check if user is logged in
 * Uses Supabase client directly to avoid AuthProvider dependency
 * Returns { isLoggedIn, authChecked, userEmail, userId } for optimistic rendering
 *
 * IMPORTANT: Subscribes to auth state changes so isLoggedIn/userEmail updates
 * immediately when user signs up during the setup flow.
 */
function useSafeLoggedInUser() {
  const $ = _c(7);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [userEmail, setUserEmail] = useState(null);
  const [userId, setUserId] = useState(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      supabaseClient.auth.getSession().then(t2 => {
        const {
          data: t3
        } = t2;
        const {
          session
        } = t3;
        setIsLoggedIn(!!session?.user?.id);
        setUserEmail(session?.user?.email ?? null);
        setUserId(session?.user?.id ?? null);
        setAuthChecked(true);
      });
      const {
        data: t4
      } = supabaseClient.auth.onAuthStateChange((event, session_0) => {
        setIsLoggedIn(!!session_0?.user?.id);
        setUserEmail(session_0?.user?.email ?? null);
        setUserId(session_0?.user?.id ?? null);
      });
      const {
        subscription
      } = t4;
      return () => subscription.unsubscribe();
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== authChecked || $[3] !== isLoggedIn || $[4] !== userEmail || $[5] !== userId) {
    t2 = {
      isLoggedIn,
      authChecked,
      userEmail,
      userId
    };
    $[2] = authChecked;
    $[3] = isLoggedIn;
    $[4] = userEmail;
    $[5] = userId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}

/**
 * Try to restore profile data from sessionStorage
 * Returns null if not found, expired, or username doesn't match
 */
function getPersistedProfileData(expectedUsername?: string): {
  profileData: ProfileData;
  aiAnalysis: AIAnalysis;
  platform: Platform;
  username: string;
} | null {
  try {
    const saved = sessionStorage.getItem(PROFILE_DATA_KEY);
    if (!saved) return null;
    const data = JSON.parse(saved);

    // Check if expired
    if (Date.now() - data.timestamp > PROFILE_DATA_MAX_AGE) {
      sessionStorage.removeItem(PROFILE_DATA_KEY);
      return null;
    }

    // If username provided, verify it matches
    if (expectedUsername && data.username !== expectedUsername) {
      return null;
    }

    // Validate essential data exists
    if (!data.profileData || !data.username) {
      return null;
    }
    return {
      profileData: data.profileData,
      aiAnalysis: data.aiAnalysis,
      platform: data.platform || "instagram",
      username: data.username
    };
  } catch (e) {
    console.error("[useSetupFlow] Failed to parse persisted profile data:", e);
    return null;
  }
}

/**
 * Persist profile data to sessionStorage
 */
function persistProfileData(platform: Platform, username: string, profileData: ProfileData, aiAnalysis: AIAnalysis | null) {
  try {
    sessionStorage.setItem(PROFILE_DATA_KEY, JSON.stringify({
      platform,
      username,
      profileData,
      aiAnalysis,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error("[useSetupFlow] Failed to persist profile data:", e);
  }
}
export function useSetupFlow(): SetupFlowState {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Safe auth check - uses Supabase directly
  // IMPORTANT: This hook subscribes to auth state changes, so isLoggedIn/userEmail
  // updates immediately when user signs up during the flow.
  const {
    isLoggedIn,
    authChecked,
    userEmail,
    userId
  } = useSafeLoggedInUser();

  // Track if we've already restored state (prevents double-restore)
  const hasRestoredState = useRef(false);
  // Track if recovery is needed (step > 0 but profile data is missing)
  const [needsRecovery, setNeedsRecovery] = useState(false);

  // Get URL params
  const urlStep = searchParams.get("step");
  const urlPlatform = searchParams.get("platform") as Platform | null;
  // ?username= is attacker/user-controlled (deep links, recovery emails, shared
  // URLs). Seed the flow ONLY with a handle the canonical normalizer accepts —
  // a pasted profile URL in the query string must not become the stored handle.
  const rawUrlUsername = searchParams.get("username");
  const parsedUrlUsername = normalizeSocialHandle(rawUrlUsername, urlPlatform === "tiktok" ? "tiktok" : "instagram");
  const urlUsername = parsedUrlUsername.ok ? parsedUrlUsername.handle : "";

  // Recovery params from abandonment emails
  const tokenHash = searchParams.get("token_hash");
  const coupon = searchParams.get("coupon");

  // Migration express checkout (null for every non-migration visit). Read once at mount
  // so the mode stays fixed for the session even as the URL is rewritten between steps.
  const [express] = useState(() => getMigrationExpress(searchParams));

  // Auto-authenticate via magic link token_hash (from recovery emails)
  useEffect(() => {
    if (!tokenHash) return;
    if (isLoggedIn) return; // Already authenticated

    supabaseClient.auth.verifyOtp({
      token_hash: tokenHash,
      type: "email"
    }).then(({
      error
    }) => {
      if (error) {
        console.error("[useSetupFlow] Magic link auth failed:", error);
        // Fall through — user can still log in manually
      }
      // Clean token_hash from URL after auth attempt (success or failure)
      const params = new URLSearchParams(window.location.search);
      params.delete("token_hash");
      const cleanUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    });
  }, [tokenHash, isLoggedIn]);

  // Initialize step from URL
  const [currentStep, setCurrentStep] = useState<SetupStep>(urlStep ? parseInt(urlStep) as SetupStep : 0);
  const [direction, setDirection] = useState(1);

  // Initialize state from URL params only (SSR-safe)
  // sessionStorage restoration happens in useEffect to avoid hydration mismatch
  const [state, setState] = useState<SetupState>(() => ({
    ...initialState,
    // A yearly-only coupon (psexpert = Glory yearly) opens on the Yearly period it discounts.
    ...(coupon && PLAN_RESTRICTED_COUPONS[coupon] && [...PLAN_RESTRICTED_COUPONS[coupon].planIds].every(isYearlyPlanId) ? {
      activePeriod: "Yearly" as const
    } : {}),
    // Migration express: the plan and period their migration link carries are preselected.
    ...(express ? {
      activePeriod: express.period,
      selectedPlanName: express.planName
    } : {}),
    platform: urlPlatform || "instagram",
    username: urlUsername || "",
    email: searchParams.get("email") || ""
  }));

  // Restore from sessionStorage after hydration (client-only)
  // This runs once on mount to restore profile data if available
  useEffect(() => {
    if (hasRestoredState.current) return;
    const persisted = getPersistedProfileData(urlUsername || undefined);
    if (persisted) {
      console.log("[useSetupFlow] Restored profile data from sessionStorage");
      hasRestoredState.current = true;
      setState(prev => ({
        ...prev,
        platform: persisted.platform,
        username: persisted.username,
        profileData: persisted.profileData,
        aiAnalysis: persisted.aiAnalysis
      }));
    }
  }, [urlUsername]);

  // Restore state from localStorage when returning from OAuth redirect
  // This runs once on mount if fromOAuth=true is in the URL
  useEffect(() => {
    const fromOAuth = searchParams.get("fromOAuth");
    if (fromOAuth === "true" && !hasRestoredState.current) {
      hasRestoredState.current = true;
      try {
        const savedStateJson = localStorage.getItem(SETUP_STATE_KEY);
        if (savedStateJson) {
          const savedState = JSON.parse(savedStateJson);
          console.log("[useSetupFlow] Restoring state from OAuth redirect:", savedState);

          // Restore the preserved state
          setState(prev_0 => ({
            ...prev_0,
            platform: savedState.platform || prev_0.platform,
            username: savedState.username || prev_0.username,
            selectedAvatarUrl: savedState.selectedAvatarUrl || prev_0.selectedAvatarUrl,
            profileData: savedState.profileData || prev_0.profileData,
            aiAnalysis: savedState.aiAnalysis || prev_0.aiAnalysis,
            activePeriod: savedState.activePeriod || prev_0.activePeriod
          }));

          // Also persist to sessionStorage for future refreshes
          if (savedState.profileData) {
            persistProfileData(savedState.platform, savedState.username, savedState.profileData, savedState.aiAnalysis);
          }

          // Clear the OAuth state now that we've restored it
          localStorage.removeItem(SETUP_STATE_KEY);

          // Clean up the URL by removing the fromOAuth param
          const params_0 = new URLSearchParams(searchParams.toString());
          params_0.delete("fromOAuth");
          const newUrl = params_0.toString() ? `${pathname}?${params_0.toString()}` : pathname;
          router.replace(newUrl, {
            scroll: false
          });
        }
      } catch (e) {
        console.error("[useSetupFlow] Failed to restore state from localStorage:", e);
      }
    }
  }, [searchParams, pathname, router]);

  // Detect when recovery is needed: step > 0 but no profile data
  // This can happen on page refresh or direct URL access
  useEffect(() => {
    // Skip if we just restored from OAuth
    if (hasRestoredState.current) return;

    // Only check after initial mount
    const stepNum = parseInt(urlStep || "0");
    if (stepNum > 0 && !state.profileData) {
      // Try to restore from sessionStorage one more time
      const persisted_0 = getPersistedProfileData(state.username || urlUsername || undefined);
      if (persisted_0) {
        console.log("[useSetupFlow] Late restore from sessionStorage");
        setState(prev_1 => ({
          ...prev_1,
          platform: persisted_0.platform,
          username: persisted_0.username,
          profileData: persisted_0.profileData,
          aiAnalysis: persisted_0.aiAnalysis
        }));
      } else if (state.username || urlUsername) {
        // Have username but no data - need to re-fetch
        console.log("[useSetupFlow] Needs recovery: have username but no profile data");
        setNeedsRecovery(true);
      } else {
        // No username and no data - redirect to step 0
        console.log("[useSetupFlow] Unrecoverable: no username and no data, redirecting to step 0");
        setCurrentStep(0);
        router.replace(pathname, {
          scroll: false
        });
      }
    }
  }, []); // Run only once on mount

  // Persist profile data to sessionStorage when it changes
  useEffect(() => {
    if (state.profileData && state.username) {
      persistProfileData(state.platform, state.username, state.profileData, state.aiAnalysis);
    }
  }, [state.profileData, state.aiAnalysis, state.platform, state.username]);

  // Update URL when step changes - includes platform for full state recovery
  const updateURL = useCallback((step: SetupStep, username?: string, platform?: Platform) => {
    const params_1 = new URLSearchParams(searchParams.toString());
    params_1.set("step", step.toString());
    if (username !== undefined) {
      if (username) {
        params_1.set("username", username);
      } else {
        params_1.delete("username");
      }
    }
    if (platform) {
      params_1.set("platform", platform);
    }
    router.replace(`${pathname}?${params_1.toString()}`, {
      scroll: false
    });
  }, [router, pathname, searchParams]);

  // Navigation actions
  const goToStep = useCallback((step_0: SetupStep) => {
    setDirection(step_0 > currentStep ? 1 : -1);
    setCurrentStep(step_0);
    updateURL(step_0, state.username, state.platform);
  }, [currentStep, updateURL, state.username, state.platform]);
  const goNext = useCallback(() => {
    let nextStep: SetupStep;
    if (currentStep === 0) {
      nextStep = 1;
    } else if (currentStep === 1) {
      // Skip account creation step if user is already logged in
      // This handles both: users who started logged in, AND users who just signed up
      nextStep = isLoggedIn ? 3 : 2;
    } else if (currentStep === 2) {
      nextStep = 3;
    } else {
      return; // Already at last step
    }
    setDirection(1);
    setCurrentStep(nextStep);
    updateURL(nextStep, state.username, state.platform);
  }, [currentStep, isLoggedIn, updateURL, state.username, state.platform]);
  const goBack = useCallback(() => {
    let prevStep: SetupStep;
    if (currentStep === 3) {
      // Skip account creation step if user is logged in
      // This handles: users who started logged in, OR users who signed up during this flow
      prevStep = isLoggedIn ? 1 : 2;
    } else if (currentStep === 2) {
      prevStep = 1;
    } else if (currentStep === 1) {
      prevStep = 0;
    } else {
      return; // Already at first step
    }
    setDirection(-1);
    setCurrentStep(prevStep);
    updateURL(prevStep, state.username, state.platform);
  }, [currentStep, isLoggedIn, updateURL, state.username, state.platform]);

  // State update actions
  const setUsername = useCallback((username_0: string) => {
    setState(prev_2 => ({
      ...prev_2,
      username: username_0,
      error: null
    }));
  }, []);
  const setPlatform = useCallback((platform_0: Platform) => {
    setState(prev_3 => ({
      ...prev_3,
      platform: platform_0,
      username: "",
      // Reset username when switching platforms
      selectedAvatarUrl: null,
      profileData: null,
      aiAnalysis: null,
      error: null
    }));
  }, []);
  const setSelectedAvatarUrl = useCallback((selectedAvatarUrl: string | null) => {
    setState(prev_4 => ({
      ...prev_4,
      selectedAvatarUrl
    }));
  }, []);
  const setEmail = useCallback((email: string) => {
    setState(prev_5 => ({
      ...prev_5,
      email,
      error: null
    }));
  }, []);
  const setPassword = useCallback((password: string) => {
    setState(prev_6 => ({
      ...prev_6,
      password,
      error: null
    }));
  }, []);
  const setActivePeriod = useCallback((activePeriod: "Monthly" | "Yearly") => {
    setState(prev_7 => ({
      ...prev_7,
      activePeriod
    }));
  }, []);
  const setSelectedPlan = useCallback((selectedPlanName: "Go" | "Grow" | "Glory") => {
    setState(prev_8 => ({
      ...prev_8,
      selectedPlanName
    }));
  }, []);
  const setProfileData = useCallback((profileData: ProfileData) => {
    setState(prev_9 => ({
      ...prev_9,
      profileData
    }));
  }, []);
  const setAIAnalysis = useCallback((aiAnalysis: AIAnalysis) => {
    setState(prev_10 => ({
      ...prev_10,
      aiAnalysis
    }));
  }, []);
  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev_11 => ({
      ...prev_11,
      isLoading
    }));
  }, []);
  const setError = useCallback((error_0: string | null) => {
    setState(prev_12 => ({
      ...prev_12,
      error: error_0
    }));
  }, []);
  const reset = useCallback(() => {
    setState(initialState);
    setCurrentStep(0);
    setDirection(1);
    router.replace(pathname, {
      scroll: false
    });
  }, [router, pathname]);

  // Memoized actions object
  const actions = useMemo(() => ({
    setUsername,
    setPlatform,
    setSelectedAvatarUrl,
    setEmail,
    setPassword,
    setActivePeriod,
    setSelectedPlan,
    setProfileData,
    setAIAnalysis,
    setLoading,
    setError,
    goToStep,
    goNext,
    goBack,
    reset
  }), [setUsername, setPlatform, setSelectedAvatarUrl, setEmail, setPassword, setActivePeriod, setSelectedPlan, setProfileData, setAIAnalysis, setLoading, setError, goToStep, goNext, goBack, reset]);

  // Action to clear recovery flag (called after successful re-fetch)
  const clearNeedsRecovery = useCallback(() => {
    setNeedsRecovery(false);
  }, []);

  // Memoized full actions object including recovery
  const fullActions = useMemo(() => ({
    ...actions,
    clearNeedsRecovery
  }), [actions, clearNeedsRecovery]);
  return {
    ...state,
    currentStep,
    direction,
    isLoggedIn,
    authChecked,
    userEmail,
    userId,
    needsRecovery,
    coupon,
    express,
    actions: fullActions
  };
}